import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';

import useDebounce from '@brands/hooks/useDebounce';
import useProviders from '@brands/hooks/useProviders';
import useCareAdvisor from '@brands/hooks/useCareAdvisor';
import SmallSelectionModal, { SmallSelectionOption } from '../SmallSelectionModal/SmallSelectionModal';
import { CaseType, ICase } from '@brands/services/cases/types/ICase';

type Props = {
  open: boolean;
  role: 'provider' | 'careAdvisor' | 'collector' | null;
  thisCase: ICase;
  onClose: () => void;
  onApply: (selectedId: string) => Promise<void>;
};

type SharedModalProps = {
  open: boolean;
  title: string;
  description?: string;
  options: SmallSelectionOption[];
  selectedValue: string;
  setSelectedValue: React.Dispatch<React.SetStateAction<string>>;
  onClose: () => void;
  onApply: (selectedId: string) => Promise<void>;
  loading: boolean;
  loadError?: string | null;
  onRetry?: () => Promise<void>;
};

const ReassignSelectionModal = ({
  open,
  title,
  description,
  options,
  selectedValue,
  setSelectedValue,
  onClose,
  onApply,
  loading,
  loadError,
  onRetry,
}: SharedModalProps): JSX.Element => {
  useEffect(() => {
    if (!options.some((option) => option.value === selectedValue)) {
      setSelectedValue('');
    }
  }, [options, selectedValue, setSelectedValue]);

  return (
    <SmallSelectionModal
      open={open}
      title={title}
      description={description}
      options={options}
      selectedValue={selectedValue}
      onChange={setSelectedValue}
      primaryLabel="Apply"
      onPrimaryClick={async () => {
        if (!selectedValue) return;
        await onApply(selectedValue);
      }}
      onClose={onClose}
      loading={loading}
      loadError={loadError}
      onRetry={onRetry}
      searchable
      searchPlaceholder="Search by name"
    />
  );
};

type ProviderModalProps = {
  open: boolean;
  thisCase: ICase;
  selectedValue: string;
  setSelectedValue: React.Dispatch<React.SetStateAction<string>>;
  onClose: () => void;
  onApply: (selectedId: string) => Promise<void>;
};

const ProviderReassignModal = ({
  open,
  thisCase,
  selectedValue,
  setSelectedValue,
  onClose,
  onApply,
}: ProviderModalProps): JSX.Element => {
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const providerParams = useMemo(() => {
    const currentLocalTime = moment().tz(browserTimeZone);
    const utcTime = currentLocalTime.utc();
    const hasConferenceDate = Boolean(thisCase.video_conference_date);
    const videoConferenceDateUTC = hasConferenceDate ? moment.utc(thisCase.video_conference_date) : null;

    const date =
      thisCase.type === CaseType.video_call_scheduled || thisCase.type === CaseType.phone_call_scheduled
        ? videoConferenceDateUTC && videoConferenceDateUTC.isValid() && !videoConferenceDateUTC.isBefore(utcTime)
          ? videoConferenceDateUTC.tz(browserTimeZone).format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD');

    return {
      date,
      tz: browserTimeZone,
      case_id: Number(thisCase.id),
      patient_id: Number(thisCase.patient_id),
      synchronous: thisCase.type === CaseType.video_call_scheduled || thisCase.type === CaseType.phone_call_scheduled,
      asynchronous: thisCase.type === CaseType.one_report || thisCase.type === CaseType.second_opinion,
      // This modal only ever asks about today; without a lookahead, a
      // provider off just today (e.g. a weekend) never appears here for
      // reassignment even with plenty of availability later this week.
      // Gated to this brand specifically because @brands falls back to
      // this file (before `default`) for every brand with no
      // ConsultationPage override of its own -- an unguarded change
      // here would silently reach all of them too.
      ...(process.env.REACT_APP_APP_BRAND === 'dentistry-one' ? { lookahead_days: 13 } : {}),
    };
  }, [browserTimeZone, thisCase.id, thisCase.patient_id, thisCase.video_conference_date, thisCase.type]);

  const debouncedProviderParams = useDebounce(providerParams, 500);
  const { providers = [], dataLoading, loadError, reloadAll } = useProviders(debouncedProviderParams);

  const options: SmallSelectionOption[] = useMemo(() => {
    return providers
      .filter((item) => item.id !== thisCase.provider_id)
      .map((item) => {
        const nextSlot = [...item.sync_time_slots, ...item.async_time_slots]
          .sort((left, right) => moment(left).valueOf() - moment(right).valueOf())[0];
        return {
          value: String(item.id),
          label: `${item.user?.full_name || ''}${nextSlot ? ` — ${moment(nextSlot).tz(browserTimeZone).format('MMM D, h:mm A z')}` : ''}`,
        };
      });
  }, [providers, thisCase.provider_id, browserTimeZone]);

  return (
    <ReassignSelectionModal
      open={open}
      title="Reassign provider"
      description={process.env.REACT_APP_APP_BRAND === 'dentistry-one'
        ? 'Showing availability over 14 days. Reassigning a provider does not change the appointment time.'
        : undefined}
      options={options}
      selectedValue={selectedValue}
      setSelectedValue={setSelectedValue}
      onClose={onClose}
      onApply={onApply}
      loading={dataLoading}
      loadError={loadError}
      onRetry={reloadAll}
    />
  );
};

type CareAdvisorModalProps = {
  open: boolean;
  thisCase: ICase;
  selectedValue: string;
  setSelectedValue: React.Dispatch<React.SetStateAction<string>>;
  onClose: () => void;
  onApply: (selectedId: string) => Promise<void>;
};

const CareAdvisorReassignModal = ({
  open,
  thisCase,
  selectedValue,
  setSelectedValue,
  onClose,
  onApply,
}: CareAdvisorModalProps): JSX.Element => {
  const params = useMemo(
    () => ({
      sort_by: 'created_at' as const,
      sort_direction: 'desc' as const,
      limit: 1000,
      case_id: thisCase.id,
    }),
    [thisCase.id],
  );

  const { careAdvisors = [], dataLoading, loadError, reloadAll } = useCareAdvisor(params, true);

  const options: SmallSelectionOption[] = useMemo(() => {
    return careAdvisors
      .filter((item) => item.id !== thisCase.care_advisor_id)
      .map((item) => ({
        value: String(item.id),
        label: item.full_name || '',
      }));
  }, [careAdvisors, thisCase.care_advisor_id]);

  return (
    <ReassignSelectionModal
      open={open}
      title="Reassign care advisor"
      loadError={loadError}
      onRetry={reloadAll}
      options={options}
      selectedValue={selectedValue}
      setSelectedValue={setSelectedValue}
      onClose={onClose}
      onApply={onApply}
      loading={dataLoading}
    />
  );
};

const ReassignRoleModal = ({ open, role, thisCase, onClose, onApply }: Props): JSX.Element => {
  const [selectedValue, setSelectedValue] = useState('');

  useEffect(() => {
    if (!open) {
      setSelectedValue('');
      return;
    }

    if (role === 'provider') {
      setSelectedValue(thisCase.provider_id ? String(thisCase.provider_id) : '');
      return;
    }

    if (role === 'careAdvisor') {
      setSelectedValue(thisCase.care_advisor_id ? String(thisCase.care_advisor_id) : '');
      return;
    }

    if (role === 'collector') {
      setSelectedValue(thisCase.collector_id ? String(thisCase.collector_id) : '');
      return;
    }

    setSelectedValue('');
  }, [open, role, thisCase.provider_id, thisCase.care_advisor_id, thisCase.collector_id]);

  if (!open || !role) {
    return <></>;
  }

  if (role === 'provider') {
    return (
      <ProviderReassignModal
        open={open}
        thisCase={thisCase}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
        onClose={onClose}
        onApply={onApply}
      />
    );
  }

  if (role === 'careAdvisor') {
    return (
      <CareAdvisorReassignModal
        open={open}
        thisCase={thisCase}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
        onClose={onClose}
        onApply={onApply}
      />
    );
  }

  return (
    <ReassignSelectionModal
      open={open}
      title="Reassign collector"
      options={[]}
      selectedValue={selectedValue}
      setSelectedValue={setSelectedValue}
      onClose={onClose}
      onApply={onApply}
      loading={false}
    />
  );
};

export default ReassignRoleModal;
